import React, { useState, useMemo } from "react";
import { useConnectionsHistory } from "@features/cx/consumerManagement/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Clock } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";

export interface consumerConnectionHistory {
  meterNumber: string;
  deviceNumber: string;
  utilityService: string;
  meterStatus: string;
  consumerNo: string;
  allocationDate: string;
  disconnectionDate: string | null;
  lastReading: number | null;
  lastReadingDate: string | null;
  firstReading: number;
  firstReadingDate: string;
  connectionLabel?: string;
  action?: string;
  actionBy?: string;
}

interface ConnectionsHistoryProps {
  consumerNo: string;
  consumerID: string;
  remoteUtilityId: string;
}

interface ConnectionsFilterProps {
  form: any;
  onApply: (data: any) => void;
  onReset: () => void;
}

const ConnectionsFilter: React.FC<ConnectionsFilterProps> = ({
  form,
  onApply,
  onReset,
}) => {
  const fields: FormField[] = [
    {
      name: "service",
      label: "Service",
      type: "select",
      placeholder: "Select Service",
      options: [
        { label: "All", value: "All" },
        { label: "Water", value: "Water" },
        { label: "Trash", value: "Trash" },
      ],
    },
  ];

  const handleReset = () => {
    form.reset({ service: "All" });
    onReset();
  };

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={fields}
        form={form}
        gridColumns={1}
        responsiveColumns={{ sm: 1, md: 1, lg: 1 }}
        onSubmit={onApply}
        onReset={handleReset}
        config={{
          showResetButton: true,
          resetButtonText: "Reset",
          submitButtonText: "Apply Filters",
          classes: {
            buttonContainer: "flex justify-end gap-2 pt-2 border-t",
          },
        }}
      />
    </div>
  );
};

export const ConnectionsHistory: React.FC<ConnectionsHistoryProps> = ({
  consumerNo,
  consumerID,
  remoteUtilityId,
}) => {
  const {
    data: connections = [],
    isLoading: loading,
    error,
    refetch,
  } = useConnectionsHistory(consumerID, remoteUtilityId);

  const [page, setPage] = useState(1);
  const [filterService, setFilterService] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const form = useForm({
    defaultValues: {
      service: "All",
    },
  });

  const handleApplyFilters = (data: { service: string }) => {
    setFilterService(data.service || "All");
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilterService("All");
    setPage(1);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const PAGE_SIZE = 10;

  const filteredConnections = useMemo(() => {
    let filtered = Array.isArray(connections) ? connections : [];

    if (filterService !== "All") {
      filtered = filtered.filter(
        (conn) =>
          conn.utilityService?.toLowerCase() === filterService.toLowerCase(),
      );
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (conn) =>
          conn.utilityService?.toLowerCase().includes(query) ||
          conn.meterNumber?.toLowerCase().includes(query) ||
          conn.deviceNumber?.toLowerCase().includes(query) ||
          conn.consumerNo?.toLowerCase().includes(query) ||
          conn.connectionLabel?.toLowerCase().includes(query) ||
          conn.action?.toLowerCase().includes(query) ||
          conn.actionBy?.toLowerCase().includes(query) ||
          conn.meterStatus?.toLowerCase().includes(query),
      );
    }

    return filtered;
  }, [connections, filterService, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredConnections.slice(start, start + PAGE_SIZE);
  }, [filteredConnections, page]);

  const totalItems = filteredConnections.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages: Math.max(1, totalPages),
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  const columns: TableColumn<consumerConnectionHistory>[] = useMemo(
    () => [
      {
        key: "utilityService",
        header: "Service",
        render: (val) => {
          if (!val) return "N/A";
          const service = val.toLowerCase();
          const isWater = service === "water";
          const isTrash = service === "trash";

          return (
            <Badge
              variant="outline"
              className={
                isWater
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : isTrash
                    ? "bg-green-50 text-green-700 border-green-200"
                    : ""
              }
            >
              {val}
            </Badge>
          );
        },
      },
      {
        key: "consumerNo",
        header: "Connection",
        render: (val, row) => {
          const label = row.connectionLabel || val;
          return <span className="text-gray-900">{label || "N/A"}</span>;
        },
      },
      {
        key: "meterNumber",
        header: "Resource",
        render: (val, row) => {
          const isActive =
            row.meterStatus?.toLowerCase().includes("active") ||
            row.meterStatus?.toLowerCase().includes("normal");

          const meterNo = val || row.meterNumber || "N/A";
          const subText = row.deviceNumber || "N/A";

          return (
            <div className="flex flex-col">
              <span
                className={`font-semibold ${isActive ? "text-blue-600" : "text-gray-900"}`}
              >
                {meterNo}
              </span>
              <span className="text-xs text-gray-500">{subText}</span>
            </div>
          );
        },
      },
      {
        key: "allocationDate",
        header: "Assigned Date",
        render: (val) => val || "N/A",
      },
      {
        key: "disconnectionDate",
        header: "Unassigned Date",
        render: (val) => {
          if (!val) return "N/A";
          return <span className="text-red-500">{val}</span>;
        },
      },
      {
        key: "action",
        header: "Action",
        render: (val, row) => {
          const actionLabel = row.action || val;
          if (!actionLabel) return "N/A";

          let badgeClass = "bg-blue-50 text-blue-700 border-blue-200";
          if (actionLabel === "Connection Created")
            badgeClass = "bg-green-50 text-green-700 border-green-200";
          if (actionLabel === "Meter Replaced")
            badgeClass = "bg-orange-50 text-orange-700 border-orange-200";
          if (actionLabel === "Qty Assigned")
            badgeClass = "bg-blue-50 text-blue-700 border-blue-200";

          return (
            <Badge variant="outline" className={badgeClass}>
              {actionLabel}
            </Badge>
          );
        },
      },
      {
        key: "actionBy",
        header: "Created by",
        render: (val, row) => <span>{row.actionBy || val || "N/A"}</span>,
      },
      {
        key: "meterStatus",
        header: "Status",
        render: (val) => {
          if (!val) return "N/A";
          const isActive =
            val.toLowerCase().includes("active") ||
            val.toLowerCase().includes("normal");
          return (
            <Badge
              variant="outline"
              className={
                isActive
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-gray-50 text-gray-700 border-gray-200"
              }
            >
              {val}
            </Badge>
          );
        },
      },
    ],
    [],
  );

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <button
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 bg-white rounded-lg border shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="h-5 w-5 text-gray-500" />
        <h2 className="text-lg font-semibold text-gray-800">
          Allocation History
        </h2>
      </div>

      <GenericTable
        data={paginatedData}
        columns={columns}
        loading={loading}
        rowKey={(row) =>
          `${row.meterNumber}-${row.deviceNumber}-${row.allocationDate}`
        }
        onSearch={handleSearch}
        searchPlaceholder="Search history..."
        pagination={pagination}
        onPageChange={setPage}
        filterPanel={
          <ConnectionsFilter
            form={form}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        }
      />
    </div>
  );
};
